/**
 * Every length, count and range limit the wire schemas enforce, each citing the
 * criterion or the database constraint it mirrors.
 *
 * They live in one module because that is what makes them auditable against the
 * schema: the database carries the same limits as `CHECK` constraints, and a
 * schema that omitted one would turn a user error into a `23514` constraint
 * violation surfacing as `internal` (design.md, "Validation failures versus
 * faults"). Stating the limit here makes the same input a rejection reported by
 * field path and key instead.
 *
 * Limits that are *configuration* rather than schema are deliberately absent:
 * the export record cap, the calendar stacking cap and the soft-delete retention
 * window live in `complan.app_config` and are read at run time, so a schema
 * carrying a copy of them would be a second source of truth.
 */
/** Initiative Title, 1 to 200 characters (R4.2, R5.6, R8.3, assumption A16). */
export const TITLE_MIN_LENGTH = 1;
/** See {@link TITLE_MIN_LENGTH}. */
export const TITLE_MAX_LENGTH = 200;
/** Initiative Details, 0 to 4,000 characters (R4.2, R5.6). */
export const DETAILS_MAX_LENGTH = 4_000;
/**
 * Every other Initiative free-text field, 0 to 1,000 characters (R5.6).
 * Mirrors `initiative_dependency.description` and
 * `initiative_ff_scope.information_contribution`.
 */
export const FREE_TEXT_MAX_LENGTH = 1_000;
/** Short free text stored in a 200-character column: dependency owner, work order. */
export const SHORT_TEXT_MAX_LENGTH = 200;
/**
 * The most Field Force activities (scope entries) one Initiative may carry,
 * across both roles. Keeps the intake form usable; real initiatives need a few.
 */
export const FIELD_FORCE_SCOPE_MAX_ENTRIES = 20;
/**
 * The free-text search term of the filter bar (UI spec 3.6).
 *
 * Bounded at the longest value the term is matched against - a Title - because a
 * term longer than every field it is compared to cannot match anything, so
 * accepting more would only widen what reaches a pattern scan.
 */
export const SEARCH_TERM_MAX_LENGTH = 200;
/** Master data `name`, 1 to 100 characters and unique among active records (R16.11). */
export const MASTER_DATA_NAME_MIN_LENGTH = 1;
/** See {@link MASTER_DATA_NAME_MIN_LENGTH}. */
export const MASTER_DATA_NAME_MAX_LENGTH = 100;
/** Master data `code`: the short label the calendar renders on a bar (R10.6). */
export const MASTER_DATA_CODE_MAX_LENGTH = 20;
/** Sales_Cycle name or number, 1 to 50 characters (R6.1). */
export const SALES_CYCLE_NAME_MIN_LENGTH = 1;
/** See {@link SALES_CYCLE_NAME_MIN_LENGTH}. */
export const SALES_CYCLE_NAME_MAX_LENGTH = 50;
/**
 * A reason recorded with a Change_Event: 1 to 500 characters excluding leading
 * and trailing whitespace (R17.2). Cancellation (R9.6) and the reason-carrying
 * transitions of R9.12 use this range.
 */
export const REASON_MIN_LENGTH = 1;
/** See {@link REASON_MIN_LENGTH}. */
export const REASON_MAX_LENGTH = 500;
/**
 * A justifying reason: 10 to 500 characters. Required for a confidentiality or
 * Explicit_Authorization_List change (R3.10) and for a manual Sales_Cycle
 * override (R7.4).
 */
export const JUSTIFICATION_MIN_LENGTH = 10;
/** See {@link JUSTIFICATION_MIN_LENGTH}. */
export const JUSTIFICATION_MAX_LENGTH = 500;
/** Optional status-transition comment, at most 1,000 characters (R9.2). */
export const TRANSITION_COMMENT_MAX_LENGTH = 1_000;
/** Input_Request text, 1 to 2,000 characters (R2.6, R2.11). */
export const INPUT_REQUEST_MIN_LENGTH = 1;
/** See {@link INPUT_REQUEST_MIN_LENGTH}. */
export const INPUT_REQUEST_MAX_LENGTH = 2_000;
/** Reference links: at most 20 per Initiative, label ≤ 200, address ≤ 2,048 (R12.4, R12.9). */
export const REFERENCE_LINK_LABEL_MAX_LENGTH = 200;
/** See {@link REFERENCE_LINK_LABEL_MAX_LENGTH}. */
export const REFERENCE_LINK_URL_MAX_LENGTH = 2_048;
/** See {@link REFERENCE_LINK_LABEL_MAX_LENGTH}. */
export const MAX_REFERENCE_LINKS_PER_INITIATIVE = 20;
/** The two schemes R12.9 permits for a reference link address. */
export const REFERENCE_LINK_SCHEMES = Object.freeze(['http:', 'https:']);
/** Saved_View name, 1 to 100 characters (R11.10). */
export const SAVED_VIEW_NAME_MIN_LENGTH = 1;
/** See {@link SAVED_VIEW_NAME_MIN_LENGTH}. */
export const SAVED_VIEW_NAME_MAX_LENGTH = 100;
/** Configurable validation rule message, 1 to 500 characters (R8.9). */
export const VALIDATION_MESSAGE_MIN_LENGTH = 1;
/** See {@link VALIDATION_MESSAGE_MIN_LENGTH}. */
export const VALIDATION_MESSAGE_MAX_LENGTH = 500;
/**
 * Planning year range, mirroring
 * `sales_cycle.planning_year check (planning_year between 2000 and 2100)`.
 * Applied to the Initiative filter year as well, so a nonsense year is rejected
 * before it reaches an indexed range scan.
 */
export const MIN_PLANNING_YEAR = 2_000;
/** See {@link MIN_PLANNING_YEAR}. */
export const MAX_PLANNING_YEAR = 2_100;
/** A manual Sales_Cycle assignment holds 1 to 12 cycles per intersected year (R7.4). */
export const MIN_MANUAL_SALES_CYCLES_PER_YEAR = 1;
/** See {@link MIN_MANUAL_SALES_CYCLES_PER_YEAR}. */
export const MAX_MANUAL_SALES_CYCLES_PER_YEAR = 12;
/** ISO 8601 week numbers a filter may name (R10.3, R18.10). */
export const MIN_ISO_WEEK = 1;
/** See {@link MIN_ISO_WEEK}. */
export const MAX_ISO_WEEK = 53;
/** Calendar months a filter may name (R11.1). */
export const MIN_MONTH = 1;
/** See {@link MIN_MONTH}. */
export const MAX_MONTH = 12;
/** Change history and Input_Request pages hold 25 entries (R12.5, R12.6). */
export const HISTORY_PAGE_SIZE = 25;
/** The Sales_Cycle impact list is paginated at 200 rows with a total count (design.md). */
export const IMPACT_PAGE_SIZE = 200;
/** Default page size of the Initiative list. */
export const INITIATIVE_PAGE_SIZE = 50;
/**
 * Largest page any paginated endpoint will serve. A bound on the response, not a
 * business rule: it keeps one request from asking for a planning year in full.
 */
export const MAX_PAGE_SIZE = 500;
/** A field path as the intake form addresses it, for example `platforms.splitPercent`. */
export const FIELD_PATH_MAX_LENGTH = 200;
/** A stable validation rule identifier, for example `HV-003` (R8.1). */
export const RULE_ID_MAX_LENGTH = 40;
/**
 * `complan-manpower` migration 18/19 `numeric` column ranges, mirroring the
 * database `CHECK` constraints so a violation is a `422` rather than a `23514`
 * surfacing as `internal` (design.md, "Validation failures versus faults").
 */
/** `effort_assumption.effort_per_unit_minutes numeric(8,2)`: strictly positive (R2.7). */
export const EFFORT_PER_UNIT_MINUTES_MIN = 0.01;
/** See {@link EFFORT_PER_UNIT_MINUTES_MIN}. `numeric(8,2)` holds up to 999,999.99. */
export const EFFORT_PER_UNIT_MINUTES_MAX = 999_999.99;
/** `effort_assumption.fixed_overhead_hours numeric(8,2)`: non-negative when entered (R2.7). */
export const FIXED_OVERHEAD_HOURS_MIN = 0;
/** See {@link FIXED_OVERHEAD_HOURS_MIN}. */
export const FIXED_OVERHEAD_HOURS_MAX = 999_999.99;
/** `capacity_period.productive_hours_per_workday numeric(4,2)`: strictly positive (R3.7). */
export const PRODUCTIVE_HOURS_PER_WORKDAY_MIN = 0.01;
/** See {@link PRODUCTIVE_HOURS_PER_WORKDAY_MIN}. `numeric(4,2)` holds up to 99.99. */
export const PRODUCTIVE_HOURS_PER_WORKDAY_MAX = 99.99;
/** `capacity_period.availability_factor numeric(3,2)`: 0.01 to 1.00 (R3.1, R3.7). */
export const AVAILABILITY_FACTOR_MIN = 0.01;
/** See {@link AVAILABILITY_FACTOR_MIN}. */
export const AVAILABILITY_FACTOR_MAX = 1;
/** `capacity_period.change_reason`: 1 to 500 characters, trimmed (R3.3, R3.6), mirroring `audit_event.reason`. */
export const CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH = 1;
/** See {@link CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH}. */
export const CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH = 500;
/** `initiative_ff_scope.impacted_population_count numeric(12,2)`: non-negative when entered (R4.4). */
export const IMPACTED_POPULATION_COUNT_MIN = 0;
/** See {@link IMPACTED_POPULATION_COUNT_MIN}. `numeric(12,2)` holds up to 9,999,999,999.99. */
export const IMPACTED_POPULATION_COUNT_MAX = 9_999_999_999.99;
/** `initiative_ff_scope.coverage_factor numeric(5,2)`: 0.00 to 100.00, 0.00 permitted (R4.4, Assumption A15). */
export const COVERAGE_FACTOR_MIN = 0;
/** See {@link COVERAGE_FACTOR_MIN}. */
export const COVERAGE_FACTOR_MAX = 100;
/** `initiative_ff_scope.frequency integer`: a positive integer once entered (R4.4). */
export const FREQUENCY_MIN = 1;
/**
 * `initiative_ff_scope.population_override_reason`: 10 to 500 characters when
 * present (R4.3), mirroring the justification-reason range Foundation already
 * uses for a confidentiality change (R3.10) and a manual Sales_Cycle override
 * (R7.4).
 */
export const POPULATION_OVERRIDE_REASON_MIN_LENGTH = 10;
/** See {@link POPULATION_OVERRIDE_REASON_MIN_LENGTH}. */
export const POPULATION_OVERRIDE_REASON_MAX_LENGTH = 500;
/** Capacity_Threshold: 50 to 99 percent, per FF role and per view (R6.6, Assumption A6). */
export const CAPACITY_THRESHOLD_MIN = 50;
/** See {@link CAPACITY_THRESHOLD_MIN}. */
export const CAPACITY_THRESHOLD_MAX = 99;
//# sourceMappingURL=limits.js.map